import { toast, Id } from 'react-toastify';

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warn',
  DEFAULT = 'default',
}

export type ToastTypeConfig = {
  type: ToastType;
  title: string;
};

export const capitalizeFirstLetter = (str: string) =>
  str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : str;

type ToastMethod = keyof Pick<
  typeof toast,
  'success' | 'error' | 'info' | 'warn'
>;

let activeToastId: Id | null = null;

export const createToast =
  (type: ToastType, title: string) => (message: string) => {
    if (activeToastId && toast.isActive(activeToastId)) {
      return;
    }

    activeToastId = toast[type as ToastMethod](
      <div>
        <strong>{title}</strong>
        <div>{message}</div>
      </div>,
      {
        onClose: () => {
          activeToastId = null;
        },
      },
    );
  };

const createToastConfig = ({ type, title }: ToastTypeConfig) =>
  createToast(type, title);

const successToast = createToastConfig({
  type: ToastType.SUCCESS,
  title: capitalizeFirstLetter(ToastType.SUCCESS),
});

const errorToast = createToastConfig({
  type: ToastType.ERROR,
  title: capitalizeFirstLetter(ToastType.ERROR),
});

const infoToast = createToastConfig({
  type: ToastType.INFO,
  title: capitalizeFirstLetter(ToastType.INFO),
});

const warningToast = createToastConfig({
  type: ToastType.WARNING,
  title: capitalizeFirstLetter(ToastType.WARNING),
});

const defaultToast = createToastConfig({
  type: ToastType.DEFAULT,
  title: 'Title',
});

function useToast() {
  return {
    [ToastType.SUCCESS]: successToast,
    [ToastType.ERROR]: errorToast,
    [ToastType.INFO]: infoToast,
    [ToastType.WARNING]: warningToast,
    [ToastType.DEFAULT]: defaultToast,
    toast,
  };
}

export default useToast;
